/**
 * @file useElectricalLayout.js
 * @description Electrical tree layout and visualization algorithms
 *
 * Handles:
 * - Matrix layout for distribution boards
 * - Smart collapsing and grouping of DBs
 * - Electrical node positioning
 * - Layout optimization
 */

import { ref, computed } from 'vue';

export function useElectricalLayout() {
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Layout mode for electrical visualization
   * @type {Ref<string>}
   */
  const electricalLayoutMode = ref('grouped'); // 'traditional', 'grouped', 'matrix', 'hybrid', 'compact'

  /**
   * Track collapsed electrical groups
   * @type {Ref<Object>}
   */
  const electricalGroupCollapsed = ref({});

  /**
   * Expanded electrical nodes tracker
   * @type {Ref<Object>}
   */
  const expandedElectricalNodes = ref({
    MSB: [],   // Array of expanded MSB IDs
    MSSB: [],  // Array of expanded MSSB/Switchgear IDs
  });

  // ============================================================================
  // MATRIX LAYOUT
  // ============================================================================

  /**
   * Apply matrix layout positioning to distribution boards
   * Arranges DBs in a grid when there are many (>6)
   *
   * @param {Object} data - Electrical hierarchy data
   * @returns {Object} Data with matrix positions applied
   */
  function applyMatrixLayout(data) {
    if (!data) {
      return data;
    }

    const processNode = (node) => {
      if (node.children) {
        const dbs = node.children.filter((child) => child.type === 'DB');
        const nonDbs = node.children.filter((child) => child.type !== 'DB');

        // Arrange DBs in matrix
        if (dbs.length > 6) {
          const cols = 5;
          dbs.forEach((db, index) => {
            db.matrixPosition = {
              row: Math.floor(index / cols),
              col: index % cols,
            };
          });
        }

        // Recursively process non-DB children
        nonDbs.forEach((child) => processNode(child));

        // Reorder children: non-DBs first, then DBs
        node.children = [...nonDbs, ...dbs];
      }

      return node;
    };

    return processNode(data);
  }

  // ============================================================================
  // SMART DB COLLAPSING
  // ============================================================================

  /**
   * Smart collapse and group distribution boards by type
   * Groups similar DBs (lighting, power, emergency, etc.) under collapsed groups
   *
   * @param {Object} node - Electrical node
   * @param {number} level - Current depth level
   * @returns {Object} Processed node with grouped DBs
   */
  function smartStackWithCollapsedDBs(node, level = 0) {
    // Clone the node
    const newNode = {
      name: node.name,
      type: node.type,
      id: node.id || `node-${Math.random().toString(36).substr(2, 9)}`,
      className: node.type ? node.type.toLowerCase() : '',
      relationship: '001',  // Has children
      children: [],
    };

    if (!node.children || node.children.length === 0) {
      newNode.relationship = '000';  // No children
      return newNode;
    }

    // For MSB nodes, group similar DBs
    if (node.type === 'MSB') {
      const dbGroups = {};
      const standaloneNodes = [];

      node.children.forEach((child) => {
        if (child.type === 'DB' || child.type === 'Distribution_Board') {
          // Determine grouping
          let groupKey = null;
          let groupName = null;

          if (child.name.match(/DB-L-\d+/)) {
            groupKey = 'lighting';
            groupName = 'Lighting Distribution';
          } else if (child.name.match(/DB-P-\d+/)) {
            groupKey = 'power';
            groupName = 'Power Distribution';
          } else if (child.name.match(/DB-EM-\d+/)) {
            groupKey = 'emergency';
            groupName = 'Emergency Distribution';
          } else if (child.name.match(/DB-CP-\d+/)) {
            groupKey = 'carpark';
            groupName = 'Carpark Distribution';
          } else if (child.name.match(/DB-EXT-\d+/)) {
            groupKey = 'external';
            groupName = 'External Distribution';
          } else if (child.name.includes('LIFT')) {
            groupKey = 'lifts';
            groupName = 'Lift Distribution';
          } else if (child.name.includes('UPS')) {
            groupKey = 'ups-db';
            groupName = 'UPS Distribution';
          } else {
            groupKey = 'other';
            groupName = 'Other Distribution';
          }

          if (groupKey) {
            if (!dbGroups[groupKey]) {
              dbGroups[groupKey] = {
                name: groupName,
                type: 'DB_GROUP',
                id: `group-${groupKey}-${Math.random().toString(36).substr(2, 9)}`,
                className: 'db-group collapsed',
                relationship: '001',
                children: [],
              };
            }
            // Add the DB as a child of the group
            dbGroups[groupKey].children.push({
              name: child.name,
              type: child.type,
              id: child.id || `node-${Math.random().toString(36).substr(2, 9)}`,
              className: child.type ? child.type.toLowerCase() : '',
              relationship: '000',  // No children
              children: [],
            });
          }
        } else {
          // Keep MSSBs, UPS, DDC expanded and visible
          standaloneNodes.push(smartStackWithCollapsedDBs(child, level + 1));
        }
      });

      // Add MSSBs and UPS first (visible)
      standaloneNodes.forEach((node) => newNode.children.push(node));

      // Then add DB groups (collapsed)
      Object.values(dbGroups).forEach((group) => {
        // Always group DBs, even if just 1
        newNode.children.push(group);
      });
    } else {
      // For other node types, recurse normally
      node.children.forEach((child) => {
        newNode.children.push(smartStackWithCollapsedDBs(child, level + 1));
      });
    }

    return newNode;
  }

  // ============================================================================
  // NODE EXPANSION
  // ============================================================================

  /**
   * Toggle electrical node expansion state
   *
   * @param {string|number} nodeId - Node ID
   * @param {string} nodeType - Node type ('MSB', 'MSSB')
   */
  function toggleElectricalNodeExpansion(nodeId, nodeType) {
    if (nodeType === 'MSB') {
      const index = expandedElectricalNodes.value.MSB.indexOf(nodeId);
      if (index === -1) {
        expandedElectricalNodes.value.MSB.push(nodeId);
      } else {
        expandedElectricalNodes.value.MSB.splice(index, 1);
      }
    } else if (nodeType === 'MSSB') {
      const index = expandedElectricalNodes.value.MSSB.indexOf(nodeId);
      if (index === -1) {
        expandedElectricalNodes.value.MSSB.push(nodeId);
      } else {
        expandedElectricalNodes.value.MSSB.splice(index, 1);
      }
    }
  }

  /**
   * Check if electrical node is expanded
   *
   * @param {string|number} nodeId - Node ID
   * @param {string} nodeType - Node type
   * @returns {boolean}
   */
  function isElectricalNodeExpanded(nodeId, nodeType) {
    if (nodeType === 'MSB') {
      return expandedElectricalNodes.value.MSB.includes(nodeId);
    } else if (nodeType === 'MSSB') {
      return expandedElectricalNodes.value.MSSB.includes(nodeId);
    }
    return false;
  }

  /**
   * Reset expansion state
   */
  function resetExpansionState() {
    expandedElectricalNodes.value = {
      MSB: [],
      MSSB: [],
    };
  }

  // ============================================================================
  // LAYOUT MODES
  // ============================================================================

  /**
   * Set electrical layout mode
   *
   * @param {string} mode - Layout mode
   */
  function setLayoutMode(mode) {
    electricalLayoutMode.value = mode;
  }

  /**
   * Toggle group collapse state
   *
   * @param {string} groupKey - Group key
   */
  function toggleGroupCollapsed(groupKey) {
    electricalGroupCollapsed.value[groupKey] = !electricalGroupCollapsed.value[groupKey];
  }

  // ============================================================================
  // DB GROUPING
  // ============================================================================

  /**
   * Group distribution boards by type (Lighting, Power, Emergency, etc.)
   * Creates grouped structure with collapsed states
   *
   * @param {Array} nodes - Array of electrical nodes
   * @returns {Array} Grouped nodes
   */
  function groupDBsByType(nodes) {
    if (!nodes || nodes.length === 0) {
      return nodes;
    }

    const groups = {
      'Lighting': [],
      'Power': [],
      'Emergency': [],
      'Vertical Transport': [],
      'Carpark': [],
      'External': [],
      'Other': [],
    };

    // Categorize DBs
    nodes.forEach((node) => {
      if (node.type === 'DB') {
        const name = node.name.toUpperCase();
        if (name.includes('DB-L-')) {
          groups['Lighting'].push(node);
        } else if (name.includes('DB-P-')) {
          groups['Power'].push(node);
        } else if (name.includes('DB-EM-')) {
          groups['Emergency'].push(node);
        } else if (name.includes('DB-LIFT') || name.includes('DB-ESC')) {
          groups['Vertical Transport'].push(node);
        } else if (name.includes('DB-CP')) {
          groups['Carpark'].push(node);
        } else if (name.includes('DB-EXT')) {
          groups['External'].push(node);
        } else {
          groups['Other'].push(node);
        }
      }
    });

    // Create grouped structure
    const groupedNodes = [];
    Object.entries(groups).forEach(([groupName, groupNodes]) => {
      if (groupNodes.length > 0) {
        groupedNodes.push({
          id: `group_${groupName.toLowerCase().replace(' ', '_')}`,
          name: `${groupName} (${groupNodes.length})`,
          type: 'GROUP',
          collapsed: electricalGroupCollapsed.value[groupName] || false,
          children: groupNodes,
        });
      }
    });

    // Add non-DB nodes directly
    nodes.forEach((node) => {
      if (node.type !== 'DB') {
        groupedNodes.push(node);
      }
    });

    return groupedNodes;
  }

  // ============================================================================
  // COLLAPSE/EXPAND PROCESSING
  // ============================================================================

  /**
   * Process electrical data with collapse/expand state applied
   * Handles MSBs with DBs and MSSBs with DDC panels
   *
   * @param {Array} nodes - Electrical tree nodes
   * @returns {Array} Processed nodes with collapse state
   */
  function processElectricalDataWithCollapse(nodes) {
    if (!nodes || nodes.length === 0) {
      return nodes;
    }

    return nodes.map((node) => {
      const processedNode = { ...node };

      // Check if this is an MSB with Distribution Boards
      if (node.brick_class === 'brick:Switchgear' && node.db_count > 0) {
        const isExpanded = expandedElectricalNodes.value.MSB.includes(node.id);

        if (!isExpanded) {
          // Filter out DB children and add count to name
          processedNode.name = `${node.name} [+] (${node.db_count} DBs)`;
          processedNode.children = (node.children || []).filter(
            (child) => child.brick_class !== 'brick:Distribution_Board',
          );
          processedNode.is_collapsed = true;
          processedNode.collapsible_type = 'DB';
          processedNode.collapsed_count = node.db_count;
        } else {
          processedNode.name = `${node.name} [-]`;
          processedNode.is_expanded = true;
        }
      }

      // Check if this is an MSSB/Switchgear with DDC Panels
      if ((node.brick_class === 'brick:Switchgear' || node.brick_class === 'brick:MSSB') && node.ddc_count > 0) {
        const isExpanded = expandedElectricalNodes.value.MSSB.includes(node.id);

        if (!isExpanded) {
          // Filter out DDC children and add count to name
          if (node.ddc_count > 0) {
            processedNode.name = processedNode.name.replace(/( \[.+\])?$/, '') + ` [+] (${node.ddc_count} DDCs)`;
            processedNode.children = (processedNode.children || []).filter(
              (child) => !['brick:DDC_Panel', 'brick:DDC_Controller'].includes(child.brick_class),
            );
            processedNode.is_collapsed = true;
            processedNode.collapsible_type = processedNode.collapsible_type ? 'DB+DDC' : 'DDC';
            processedNode.collapsed_ddc_count = node.ddc_count;
          }
        } else if (node.ddc_count > 0) {
          // Show expanded indicator for DDCs
          processedNode.name = processedNode.name.replace(' [+]', ' [-]');
          processedNode.is_expanded = true;
        }
      }

      // Recursively process children
      if (processedNode.children && processedNode.children.length > 0) {
        processedNode.children = processElectricalDataWithCollapse(processedNode.children);
      }

      return processedNode;
    });
  }

  /**
   * Expand electrical tree to specific level
   * level 2: Only transformers
   * level 3: Up to MSSB level (Transformer -> MSB -> MSSB)
   * level 999: Expand all
   *
   * @param {number} level - Level to expand to
   * @param {Array} treeNodes - Tree nodes to expand
   * @returns {Array} Array of expanded node IDs
   */
  function expandElectricalToLevel(level, treeNodes) {
    if (!treeNodes || treeNodes.length === 0) {
      return [];
    }

    const expandedIds = [];

    const collectNodesAtLevel = (nodes, currentLevel = 1) => {
      if (!nodes) {
        return;
      }

      nodes.forEach((node) => {
        // Expand nodes up to the specified level
        if (currentLevel < level) {
          expandedIds.push(node.id);
        }

        // Recursively process children
        if (node.children && node.children.length > 0) {
          collectNodesAtLevel(node.children, currentLevel + 1);
        }
      });
    };

    if (level === 2) {
      // Collapse all - only expand transformers
      treeNodes.forEach((node) => {
        expandedIds.push(node.id);
      });
    } else if (level === 3) {
      // Expand to MSSB level (Transformer -> MSB -> MSSB)
      collectNodesAtLevel(treeNodes, 1);
    } else if (level === 999) {
      // Expand all
      const collectAllIds = (nodes) => {
        if (!nodes) {
          return;
        }
        nodes.forEach((node) => {
          expandedIds.push(node.id);
          if (node.children) {
            collectAllIds(node.children);
          }
        });
      };
      collectAllIds(treeNodes);
    }

    return expandedIds;
  }

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Is grouped layout mode active
   */
  const isGroupedLayout = computed(() => {
    return electricalLayoutMode.value === 'grouped';
  });

  /**
   * Is matrix layout mode active
   */
  const isMatrixLayout = computed(() => {
    return electricalLayoutMode.value === 'matrix';
  });

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    electricalLayoutMode,
    electricalGroupCollapsed,
    expandedElectricalNodes,

    // Computed
    isGroupedLayout,
    isMatrixLayout,

    // Layout functions
    applyMatrixLayout,
    smartStackWithCollapsedDBs,
    groupDBsByType,
    processElectricalDataWithCollapse,

    // Expansion functions
    toggleElectricalNodeExpansion,
    isElectricalNodeExpanded,
    resetExpansionState,
    expandElectricalToLevel,

    // Layout mode functions
    setLayoutMode,
    toggleGroupCollapsed,
  };
}
